import { Link, matchPath, useLocation } from "react-router";
import { my_routes } from "../router/all_routes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./finance.css";
import withPermission from "../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  Resource,
} from "../../core/common/autorisations/permissionsMap";
import { usePermissions } from "../../core/common/context/PermissionsUserContext";
import { useSelector } from "react-redux";
import PaymentSettingsModal from "./settings";
import { useState } from "react";

type FinanceButton = {
  type?: "button"; // bouton simple
  route: string;
  icon: string;
  label: string;
  resource: Resource;
  actions: Action[];
};

type FinanceDropdownItem = {
  route: string;
  icon: string;
  label: string;
  resource: Resource;
  actions: Action[];
};

type FinanceDropdown = {
  type: "dropdown"; // dropdown
  label: string;
  icon: string;
  items: FinanceDropdownItem[];
};

type FinanceMenuItem = FinanceButton | FinanceDropdown;

const FinanceMenu = () => {
  const routes = my_routes;
  const location = useLocation();

  const [showSettings, setShowSettings] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // les permissions de l'utilisateur connecté
  const { permissions } = usePermissions();

  //Reload de les paramètres après sauvegarde
  const reloadDataSettings = () => setRefreshKey((prev) => prev + 1);

  const isSuperAdmin = useSelector(
    (state: any) => state.auth.user.isSuperAdmin,
  );

  const isActive = (path: string) => {
    return matchPath({ path, end: true }, location.pathname);
  };

  const financeConfig: FinanceMenuItem[] = [
    {
      type: "button",
      route: routes.allPayment,
      icon: "ti ti-cash",
      label: "Paiements",
      resource: Resource.PAYMENT,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.PaymentPlan,
      icon: "ti ti-wallet",
      label: "Échéancier",
      resource: Resource.PAYMENT_PLAN,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.bourseAndRemise,
      icon: "ti ti-users-group",
      label: "Bourse & Rémises",
      resource: Resource.BOURSE_REMISE,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: routes.restrictionNote,
      icon: "ti ti-shield-lock",
      label: "Restrictions Notes",
      resource: Resource.NOTE_RESTRICTION,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
    {
      type: "button",
      route: "#",
      icon: "ti ti-adjustments",
      label: "Paramètres",
      resource: Resource.PAYMENT_SETTING,
      actions: [Action.CREATE, Action.READ, Action.UPDATE, Action.DELETE],
    },
  ];

  const FinanceItem = ({ item }: { item: FinanceMenuItem }) => {
    // Dropdown ?
    if (item.type === "dropdown") {
      // Ne garder que les sous-éléments que l'utilisateur peut voir
      const visibleSubItems = item.items.filter(
        (sub) =>
          Object.values(Action).some((a) =>
            permissions.includes(buildPermission(sub.resource, a)),
          ) || isSuperAdmin,
      );

      // Si aucun sous-élément n'est visible, ne pas afficher le dropdown
      if (visibleSubItems.length === 0) return null;

      const isActiveDropdown = visibleSubItems.some((i) => isActive(i.route));

      return (
        <div className="finance-menu-item">
          <div className="dropdown h-100 dashboard-dropdown">
            <div
              className={`finance-nav-link dashboard-btn dropdown-toggle ${
                isActiveDropdown ? "active" : ""
              }`}
              data-bs-toggle="dropdown"
              style={{ cursor: "pointer" }}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </div>
            <ul className="dropdown-menu finance-dropdown-menu">
              {visibleSubItems.map((sub) => (
                <Link
                  key={sub.route}
                  className={`dropdown-item d-flex align-items-center ${
                    isActive(sub.route) ? "active" : ""
                  }`}
                  to={sub.route}
                >
                  <i className={`${sub.icon} me-2`} />
                  <span>{sub.label}</span>
                </Link>
              ))}
            </ul>
          </div>
        </div>
      );
    }

    // Bouton simple
    const ProtectedButton = withPermission(
      () => (
        <div
          className={`finance-menu-item ${item.label === "Paramètres" ? "settings" : ""}`}
        >
          {item.label === "Paramètres" ? (
            // 👉 CAS PARAMÈTRES (ouvre le modal)
            <button
              type="button"
              className={`finance-nav-link finance-settings-btn dashboard-btn ${
                showSettings ? "active" : ""
              }`}
              onClick={() => setShowSettings(true)}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </button>
          ) : (
            // 👉 AUTRES BOUTONS (navigation normale)
            <Link
              to={item.route}
              className={`finance-nav-link dashboard-btn ${
                isActive(item.route) ? "active" : ""
              }`}
            >
              <i className={item.icon} />
              <span>{item.label}</span>
            </Link>
          )}
        </div>
      ),
      item.actions.map((a) => buildPermission(item.resource, a)),
      "masquer",
    );

    return <ProtectedButton />;
  };

  return (
    <>
      <div className="finance-module-menu">
        <div className="finance-module-menu-main">
          {financeConfig
            .filter(
              (item) => item.type === "dropdown" || item.label !== "Paramètres",
            )
            .map((item: FinanceMenuItem) => (
              <FinanceItem key={item.label} item={item} />
            ))}
        </div>
        <div className="finance-module-menu-actions">
          {financeConfig
            .filter(
              (item) => item.type !== "dropdown" && item.label === "Paramètres",
            )
            .map((item: FinanceMenuItem) => (
              <FinanceItem key={item.label} item={item} />
            ))}
        </div>
      </div>

      <PaymentSettingsModal
        show={showSettings}
        onClose={() => setShowSettings(false)}
        onSaved={reloadDataSettings}
        setShow={setShowSettings}
      />
    </>
  );
};

export default FinanceMenu;
